import React, { useCallback } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Link } from '@opendoor/atoms/link';
import { Text } from '@opendoor/atoms/text';
import { IconThumbDown, IconThumbUp } from '@tabler/icons-react';

import { HubPropertyPhoto } from './HubPropertyPhoto';
import type { HubSectionId } from './hubSectionOrdering';
import { TOUR_CANCEL_COPY } from './TourCancelDialog';
import { TOUR_FEEDBACK_NEGATIVE, TOUR_FEEDBACK_POSITIVE } from './tourFeedback';
import type { QuickTourFeedbackSentiment } from './tourFeedback';
import type { HubTour } from './useHomeTours';
import { useHubSectionTracking } from './useHubSectionTracking';

const isPresent = (line: string | null | undefined): line is string => Boolean(line);

type TourStatus = 'upcoming' | 'completed' | 'canceled';

const TOUR_STATUS_LABEL: Record<TourStatus, string> = {
  upcoming: 'Upcoming',
  completed: 'Completed',
  canceled: TOUR_CANCEL_COPY.canceledBadge,
};

const TourStatusBadge: React.FC<{ status: TourStatus }> = ({ status }) => {
  const isMuted = status !== 'upcoming';
  return (
    <Box
      className={`absolute top-2 left-2 rounded-sm px-1.5 py-[3px] ${isMuted ? 'bg-od-bg-tertiary' : 'bg-od-bg-primary'}`}
    >
      <Text
        as="span"
        className={`text-label-sm ${isMuted ? 'text-od-content-secondary' : 'text-od-content-primary'}`}
      >
        {TOUR_STATUS_LABEL[status]}
      </Text>
    </Box>
  );
};

const tourStatus = (tour: HubTour, isCanceled: boolean): TourStatus => {
  if (isCanceled) {
    return 'canceled';
  }
  return tour.isPast ? 'completed' : 'upcoming';
};

interface ReactionChipProps {
  tour: HubTour;
  sentiment: QuickTourFeedbackSentiment;
  selected: boolean;
  onFeedback?(tour: HubTour, sentiment: QuickTourFeedbackSentiment): void;
  onSectionInteraction(): void;
}

function ReactionChip({
  tour,
  sentiment,
  selected,
  onFeedback,
  onSectionInteraction,
}: ReactionChipProps) {
  const isPositive = sentiment === TOUR_FEEDBACK_POSITIVE;
  const Icon = isPositive ? IconThumbUp : IconThumbDown;
  const label = isPositive ? 'Like this past tour' : 'Dislike this past tour';

  const selectedTextClass = isPositive ? 'text-od-content-success' : 'text-od-content-error';
  // No Atoms token for the inactive-accent-success tint; approximate with a low-opacity success fill.
  const selectedBgClass = isPositive ? 'bg-od-bg-success/10' : 'bg-od-bg-error-secondary';
  const chipClass = `h-[34px] w-[34px] items-center justify-center rounded-lg ${
    selected ? selectedBgClass : 'bg-od-bg-tertiary'
  }`;

  const content = (
    <Icon size={18} className={selected ? selectedTextClass : 'text-od-content-primary'} />
  );

  const submitFeedback = useCallback(
    function submitFeedback() {
      onSectionInteraction();
      if (!onFeedback) {
        return;
      }

      onFeedback(tour, sentiment);
    },
    [onFeedback, onSectionInteraction, sentiment, tour],
  );

  if (!onFeedback) {
    return <Box className={chipClass}>{content}</Box>;
  }

  return (
    <Box
      analyticsName={isPositive ? 'hub-past-tour-like' : 'hub-past-tour-dislike'}
      aria-label={label}
      aria-pressed={selected}
      onClick={submitFeedback}
      className={`${chipClass} cursor-pointer`}
    >
      {content}
    </Box>
  );
}

interface TourCardProps {
  section: HubSectionId;
  tour: HubTour;
  feedback?: QuickTourFeedbackSentiment;
  onFeedback?(tour: HubTour, sentiment: QuickTourFeedbackSentiment): void;
  isCanceled?: boolean;
  onCancel?: (tour: HubTour) => void;
}

const MOBILE_SCHEDULE_LINE: Record<TourStatus, (tour: HubTour) => string> = {
  upcoming: (tour) => `${tour.timeRange} · ${tour.dateLabel}`,
  completed: (tour) => tour.dateLabel,
  canceled: (tour) => `${TOUR_CANCEL_COPY.canceledSchedulePrefix} · ${tour.dateLabel}`,
};

function TourCardLinkArea({
  tour,
  status,
  hasCancelAction,
  onSectionInteraction,
}: {
  tour: HubTour;
  status: TourStatus;
  hasCancelAction: boolean;
  onSectionInteraction(): void;
}) {
  const infoLines = [tour.priceLabel, tour.detailsLine].filter(isPresent);
  // Mobile folds the schedule into the info column; desktop renders it in a
  // right-aligned meta column in `TourCard`, outside this link, so the meta
  // column can hold the cancel and feedback controls.
  const mobileScheduleLine = MOBILE_SCHEDULE_LINE[status](tour);

  const content = (
    <Col className="w-full md:flex-row md:items-stretch md:gap-4">
      <Box className="relative h-[161px] w-full shrink-0 overflow-hidden rounded-xl md:h-[131px] md:w-[197px] md:self-center">
        <HubPropertyPhoto photoUrl={tour.photoUrl} />
        <TourStatusBadge status={status} />
      </Box>
      <Col className="min-w-0 flex-1 justify-center gap-1 py-3 md:py-2">
        <Text as="span" className="truncate text-label-md text-od-content-primary">
          {tour.addressLine}
        </Text>
        {infoLines.map((line) => (
          <Text key={line} as="span" className="truncate text-body-sm text-od-content-secondary">
            {line}
          </Text>
        ))}
        <Text
          as="span"
          className={`text-body-sm text-od-content-secondary md:hidden ${hasCancelAction ? 'pr-16' : ''}`}
        >
          {mobileScheduleLine}
        </Text>
      </Col>
    </Col>
  );

  return (
    <Box
      href={tour.href}
      analyticsName="hub-tour-card"
      aria-label={`View ${tour.addressLine}`}
      onClick={onSectionInteraction}
      className="min-w-0 flex-1 no-underline"
    >
      {content}
    </Box>
  );
}

export function TourCard({
  section,
  tour,
  feedback,
  onFeedback,
  isCanceled = false,
  onCancel,
}: TourCardProps) {
  const trackSectionInteraction = useHubSectionTracking(section);
  const status = tourStatus(tour, isCanceled);
  const showCancel = status === 'upcoming' && tour.canCancel && onCancel !== undefined;

  const handleCancelClick = useCallback(
    function handleCancelClick() {
      trackSectionInteraction();
      onCancel?.(tour);
    },
    [onCancel, tour, trackSectionInteraction],
  );

  return (
    <Col className="relative w-full items-stretch gap-3 md:flex-row md:items-center md:gap-4">
      <TourCardLinkArea
        tour={tour}
        status={status}
        hasCancelAction={showCancel}
        onSectionInteraction={trackSectionInteraction}
      />
      {!tour.isPast && (
        <Col
          // On mobile the cancel link sits on the schedule line, which is inside the card link
          // and so cannot contain it; bottom-3 matches the info column's bottom padding.
          className={`shrink-0 items-end justify-center gap-1 md:static md:self-stretch ${showCancel ? 'absolute right-0 bottom-3' : 'hidden md:flex'}`}
        >
          <Text as="span" className="hidden text-body-sm text-od-content-secondary md:flex">
            {status === 'canceled'
              ? `${TOUR_CANCEL_COPY.canceledSchedulePrefix} · ${tour.dateLabel}`
              : tour.dateLabel}
          </Text>
          {status === 'upcoming' && (
            <Text as="span" className="hidden text-body-sm text-od-content-secondary md:flex">
              {tour.timeRange}
            </Text>
          )}
          {showCancel && (
            <Link
              render={<button type="button" />}
              className="cursor-pointer text-body-sm no-underline!"
              onClick={handleCancelClick}
              aria-label={`${TOUR_CANCEL_COPY.action} at ${tour.addressLine}`}
              analyticsName="hub-tour-cancel"
            >
              {TOUR_CANCEL_COPY.link}
            </Link>
          )}
        </Col>
      )}
      {tour.isPast && (
        <Col className="shrink-0 items-end justify-center gap-2 self-stretch">
          <Text as="span" className="hidden text-body-sm text-od-content-secondary md:flex">
            {tour.dateLabel}
          </Text>
          <Row className="items-center gap-2">
            <ReactionChip
              tour={tour}
              sentiment={TOUR_FEEDBACK_POSITIVE}
              selected={feedback === TOUR_FEEDBACK_POSITIVE}
              onFeedback={onFeedback}
              onSectionInteraction={trackSectionInteraction}
            />
            <ReactionChip
              tour={tour}
              sentiment={TOUR_FEEDBACK_NEGATIVE}
              selected={feedback === TOUR_FEEDBACK_NEGATIVE}
              onFeedback={onFeedback}
              onSectionInteraction={trackSectionInteraction}
            />
          </Row>
        </Col>
      )}
    </Col>
  );
}
